import './QuotePage.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Alert from '../../../shared/components/Alert/Alert';
import Box from '../../../shared/components/Box/Box';
import { getAllProjects } from '../../projects/project.api';
import { getAffair, getAffairs, getAffairPlanningQuoteDraft } from '../../crm/affairs/affair.api';
import {
  getQuote,
  getQuotePermissions,
  getQuoteVersion,
  getQuotes,
  getQuoteCustomerTerms,
  runQuoteVersionCommand,
  enableQuoteComposition,
} from '../quote.api';
import QuoteEditor from '../components/QuoteEditor';
import useOperationalComposition from '../composition/useOperationalComposition';
import QuoteFolderView from '../components/QuoteFolderView';
import QuoteList from '../components/QuoteList';
import {
  editorFromQuote,
  formatMoney,
  getProjectName,
  getQuoteProject,
  planningDraftToQuoteSections,
} from '../quoteEditorModel';
import useQuoteCalculation from '../hooks/useQuoteCalculation';
import useQuoteDocuments from '../hooks/useQuoteDocuments';
import useQuoteEditor from '../hooks/useQuoteEditor';
import useQuoteFolder from '../hooks/useQuoteFolder';
import useQuoteVersionCommands from '../hooks/useQuoteVersionCommands';
import useQuoteRevisionConflict from '../hooks/useQuoteRevisionConflict';
import useQuoteSave from '../hooks/useQuoteSave';
import useQuoteEditorOperation from '../hooks/useQuoteEditorOperation';
import useQuoteEditorNavigation from '../hooks/useQuoteEditorNavigation';
import useQuoteDocumentExport from '../hooks/useQuoteDocumentExport';

function QuotePage() {
  return <LiveQuotePage />;
}

function LiveQuotePage() {
  const { id: routeProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const routeAffairId = searchParams.get('affairId') || '';
  const routeQuoteId = searchParams.get('quoteId') || '';
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [affairs, setAffairs] = useState([]);
  const [printAffair, setPrintAffair] = useState(null);
  const [viewMode, setViewMode] = useState('internal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [pagePermissions, setPagePermissions] = useState({});
  const [compositionAccessStatus, setCompositionAccessStatus] = useState('loading');
  const [compositionAccessRefresh, setCompositionAccessRefresh] = useState(0);
  const [planningQuoteDraft, setPlanningQuoteDraft] = useState(null);
  const [planningQuoteDraftLoading, setPlanningQuoteDraftLoading] = useState(false);
  const {
    folder, folderVersions, folderDocuments, folderHistory,
    loadFolder, clearFolder,
  } = useQuoteFolder();
  const {
    editor, setEditor, openNewEditor, updateEditor, applyCustomerSnapshot, applyPartnerTerms,
    updateVersionParameters, updateVersionExpenses, updateLine, applyCatalogMaterial,
    addLine: addVisibleLine, addTextLine, addSection, updateSection, removeSection, importSections,
    duplicateLine, moveLine, removeLine,
    addOption, updateOption, removeOption, clearOptions, addOptionLine, updateOptionLine, removeOptionLine,
  } = useQuoteEditor({ affairs, pagePermissions });
  const composition = useOperationalComposition({ editor, setEditor });
  const compositionEditorKey = editor ? `${editor.id || 'new'}:${editor.quoteVersionId || 'new'}` : null;
  useEffect(() => {
    if (!compositionEditorKey) return undefined;
    let active = true, sequence = 0;
    const refresh = async () => {
      const request = ++sequence;
      setCompositionAccessStatus('loading');
      try {
        const permissions = await getQuotePermissions();
        if (active && request === sequence) {
          setPagePermissions(permissions || {});
          setCompositionAccessStatus('ready');
        }
      } catch (_) { if (active && request === sequence) setCompositionAccessStatus('error'); }
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => { active = false; window.removeEventListener('focus', refresh); };
  }, [compositionEditorKey, compositionAccessRefresh]);
  const editorOperation = useQuoteEditorOperation({ editor, setEditor, setSaving, setError, setStatusMessage });
  const editorNavigation = useQuoteEditorNavigation({ editor, setEditor, setError, pendingComposition: composition?.hasPendingChanges });
  const {
    calculated, visibleLines, calculationStatus, calculationError, calculationRequest,
  } = useQuoteCalculation(editor);
  const selectedProject = useMemo(
    () => projects.find((project) => String(project.id) === String(editor?.projectId || '')) || null,
    [projects, editor?.projectId]
  );
  const selectedAffair = useMemo(() => {
    const affairId = editor?.affairId
      || editor?.affair_id
      || folder?.affairId
      || folder?.affair_id
      || selectedProject?.affairId
      || selectedProject?.affair_id
      || routeAffairId
      || '';

    if (affairId) {
      const byId = affairs.find((affair) => String(affair.id) === String(affairId));
      if (byId) return byId;
    }

    // Legacy/existing quotes may not expose affairId even though they were created
    // from an Affair. In that case, use the client/company relation only when it is
    // unambiguous. This keeps the print context deterministic and avoids attaching
    // dates from the wrong Affair when a client has several dossiers.
    const companyId = editor?.companyId || editor?.company_id || '';
    if (companyId) {
      const companyAffairs = affairs.filter((affair) => String(
        affair.companyId || affair.company_id || ''
      ) === String(companyId));
      if (companyAffairs.length === 1) return companyAffairs[0];
    }

    return null;
  }, [
    affairs,
    editor?.affairId,
    editor?.affair_id,
    editor?.companyId,
    editor?.company_id,
    folder?.affairId,
    folder?.affair_id,
    selectedProject,
    routeAffairId,
  ]);
  const hasEditor = Boolean(editor);
  const editorId = editor?.id;
  const editorCompanyId = editor?.companyId;
  const partnerQuoteSettingsSource = editor?.partnerQuoteSettingsSource;
  const planningAffairId = editor?.affairId || editor?.affair_id || folder?.affairId || folder?.affair_id || routeAffairId || '';
  const printAffairId = editor?.affairId
    || editor?.affair_id
    || folder?.affairId
    || folder?.affair_id
    || selectedProject?.affairId
    || selectedProject?.affair_id
    || routeAffairId
    || selectedAffair?.id
    || '';


  useEffect(() => {
    if (!planningAffairId || !hasEditor) {
      setPlanningQuoteDraft(null);
      setPlanningQuoteDraftLoading(false);
      return undefined;
    }
    let active = true;
    setPlanningQuoteDraftLoading(true);
    getAffairPlanningQuoteDraft(planningAffairId)
      .then((draft) => { if (active) setPlanningQuoteDraft(draft || null); })
      .catch(() => { if (active) setPlanningQuoteDraft(null); })
      .finally(() => { if (active) setPlanningQuoteDraftLoading(false); });
    return () => { active = false; };
  }, [planningAffairId, hasEditor]);

  useEffect(() => {
    if (!printAffairId) {
      setPrintAffair(selectedAffair || null);
      return undefined;
    }

    let active = true;
    getAffair(printAffairId)
      .then((row) => {
        if (active) setPrintAffair(row || selectedAffair || null);
      })
      .catch(() => {
        if (active) setPrintAffair(selectedAffair || null);
      });

    return () => { active = false; };
  }, [printAffairId, selectedAffair]);

  useEffect(() => {
    if (!statusMessage) return undefined;
    const timeoutId = window.setTimeout(() => setStatusMessage(null), 10000);
    return () => window.clearTimeout(timeoutId);
  }, [statusMessage]);


  useEffect(() => {
    const companyId = editorCompanyId;
    if (!hasEditor || editorId || !companyId) return;
    const expectedSource = `partner:${companyId}:quote-settings`;
    if (partnerQuoteSettingsSource === expectedSource) return;
    let active = true;
    getQuoteCustomerTerms(companyId)
      .then((terms) => {
        if (active && terms?.enabled !== false) applyPartnerTerms(companyId, terms);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [hasEditor, editorId, editorCompanyId, partnerQuoteSettingsSource, applyPartnerTerms]);

  const loadQuotes = async () => {
    const data = await getQuotes();
    setQuotes(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [quoteRows, projectRows, affairRows, permissions] = await Promise.all([
          getQuotes(),
          getAllProjects(),
          getAffairs(),
          getQuotePermissions(),
        ]);

        setQuotes(Array.isArray(quoteRows) ? quoteRows : []);
        setProjects(Array.isArray(projectRows) ? projectRows : projectRows?.data || []);
        setAffairs(Array.isArray(affairRows) ? affairRows : []);
        setPagePermissions(permissions || {});
        if (routeQuoteId) await loadFolder(routeQuoteId);
        setError(null);
      } catch (err) {
        setError(err.message || 'Impossible de charger les devis');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [routeQuoteId, loadFolder]);

  const openNewQuote = () => {
    const leaving = editorNavigation.begin();
    if (!leaving) return;
    clearFolder();
    openNewEditor(routeProjectId || '', routeAffairId);
    setViewMode('internal');
    setStatusMessage(null);
    setError(null);
  };

  const openExistingQuote = async (quoteId) => {
    const leaving = editorNavigation.begin();
    if (!leaving) return;
    try {
      setLoading(true);
      // Close only the editor that the user agreed to leave, before loading.
      if (!editorNavigation.apply(leaving, null)) return;
      await loadFolder(quoteId);
      setStatusMessage(null);
      setError(null);
    } catch (err) {
      setError(err.message || 'Impossible de charger le devis');
    } finally {
      setLoading(false);
    }
  };

  const closeEditor = () => {
    const leaving = editorNavigation.begin();
    if (!leaving || !editorNavigation.apply(leaving, null)) return;
    setStatusMessage(null);
  };

  const openFolderVersion = async (versionId) => {
    const leaving = editorNavigation.begin();
    if (!leaving) return;
    try {
      setLoading(true);
      const quote = await getQuoteVersion(folder.id, versionId, folder);
      if (!editorNavigation.apply(leaving, editorFromQuote(quote))) return;
      setViewMode('internal');
      setError(null);
    } catch (err) {
      setError(err.message || 'Impossible de charger cette version');
    } finally {
      setLoading(false);
    }
  };

  const modifyCurrentVersion = async (versionId) => {
    const leaving = editorNavigation.begin();
    if (!leaving) return;
    try {
      setLoading(true);
      const source = folderVersions.find((version) => String(version.id) === String(versionId));
      await runQuoteVersionCommand(folder.id, versionId, 'clone', {}, source?.revision);
      if (!editorNavigation.current(leaving)) return;
      await loadFolder(folder.id);
      const quote = await getQuote(folder.id);
      if (!editorNavigation.apply(leaving, editorFromQuote(quote))) return;
      setStatusMessage('Une nouvelle version modifiable a été créée.');
    } catch (err) {
      setError(err.message || 'Impossible de créer une nouvelle version');
    } finally {
      setLoading(false);
    }
  };

  const { revisionConflict, reportRevisionError, reloadAfterConflict } = useQuoteRevisionConflict({
    editor, setEditor, setSaving, setError, onReloaded: composition?.clearPending,
  });
  const { forceLineValue, resetLineValue, runVersionCommand } = useQuoteVersionCommands({
    editorOperation,
    editor,
    folder,
    setEditor,
    setSaving,
    setError,
    setStatusMessage,
    loadFolder,
    loadQuotes,
    reportRevisionError,
  });
  const { previewDocument, generateOfficialDocument, archiveDocumentToGed, downloadDocument } = useQuoteDocuments({
    editorOperation,
    editor,
    folder,
    setEditor,
    setSaving,
    setError,
    setStatusMessage,
    loadFolder,
  });

  const printContext = {
    editor,
    calculated,
    project: selectedProject,
    affair: printAffair || selectedAffair,
    formatMoney,
    calculationStatus,
    pendingComposition: composition?.hasPendingChanges,
  };
  const { previewPrintTemplate, printPdf, previewInternalTemplate, printInternalTemplate } = useQuoteDocumentExport({ context: printContext, setError });

  const importPlanningIntoQuote = () => {
    if (!editor?.permissions?.canEdit) return;
    if (!planningQuoteDraft?.workItemCount) {
      setError('Le planning AO / DCE ne contient aucune charge ICC à importer.');
      return;
    }
    if (composition?.operational) {
      const target = composition.line?.structureKind === 'composite' ? composition.line : null;
      if (!window.confirm(target ? 'Importer les livrables comme enfants de cet ensemble ? Leur quantité locale sera 1 et les heures totales du planning seront conservées pour l’ensemble complet.' : 'Importer les livrables en lignes simples, avec le profil Études et son tarif réel à la date du jour ?')) return;
      const placement = target ? { groups: [], items: Object.fromEntries((planningQuoteDraft.phases || []).flatMap(phase => (phase.lots || []).flatMap(lot => (lot.items || []).map(item => [item.planningKey, { parentKey: null, quantity: '1', hoursBasis: 'effective_total' }])))) } : undefined;
      composition.run({ type: 'importPlanning', scope: composition.entry.scope, mode: target ? 'structured' : 'classic',
        ...(target ? { targetSectionId: target.quoteSectionId, parentLineId: target.id, structureConfirmed: true, placement } : {}),
        sourceRef: { affairId: planningAffairId, effectiveDate: new Date().toISOString().slice(0, 10) }, vatRate: String(editor.defaultVatRate ?? '20') });
      return;
    }
    const currentLines = (editor.sections || []).flatMap((section) => section.lines || []);
    const existingPlanningKeys = new Set(currentLines.map((line) => line.planningSourceKey).filter(Boolean));
    const incomingPlanningKeys = (planningQuoteDraft.phases || []).flatMap((phase) => (phase.lots || []).flatMap((lot) => (lot.items || []).map((item) => item.planningKey).filter(Boolean)));
    const duplicates = incomingPlanningKeys.filter((key) => existingPlanningKeys.has(key));
    if (duplicates.length) {
      setError(`Ce devis contient déjà ${duplicates.length} prestation(s) issue(s) de ce planning. L’import est bloqué pour éviter les doublons.`);
      return;
    }
    const sections = planningDraftToQuoteSections(planningQuoteDraft, editor);
    const isDefaultDraft = (editor.sections || []).length === 1 && currentLines.length === 0;
    if (!isDefaultDraft && !window.confirm(`Le devis contient déjà ${currentLines.length} ligne(s). Ajouter les ${planningQuoteDraft.workItemCount} prestation(s) du planning AO / DCE ?`)) return;
    importSections(sections, isDefaultDraft);
    setStatusMessage(`${planningQuoteDraft.workItemCount} prestation(s) importée(s) depuis le planning AO / DCE - ${Number(planningQuoteDraft.totalEstimatedHours || 0).toFixed(2)} h.`);
    setError(null);
  };

  const saveQuote = useQuoteSave({ editor, calculationRequest, setSaving, setError, setEditor,
    setStatusMessage, loadFolder, loadQuotes, reportRevisionError, composition });

  const filteredQuotes = routeProjectId
    ? quotes.filter((quote) => String(quote.projectId || quote.project_id || '') === String(routeProjectId))
    : routeAffairId
      ? quotes.filter((quote) => String(quote.affairId || quote.affair_id || '') === String(routeAffairId))
      : quotes;

  return (
    <Box className="page-container quote-page">
      {error && <Alert severity="error">{error}{revisionConflict && <button className="basic-button" type="button" disabled={saving} onClick={reloadAfterConflict}>Recharger la version actuelle</button>}</Alert>}
      {statusMessage && <Alert severity="success">{statusMessage}</Alert>}

      {editor ? (
        <QuoteEditor
          editor={editor}
          composition={composition}
          compositionAccess={{ status: compositionAccessStatus, permissions: pagePermissions,
            onRefresh: () => setCompositionAccessRefresh(value => value + 1) }}
          onEnableComposition={async () => {
            if (!window.confirm('Activer les ensembles composés pour ce brouillon ? Corrigez d’abord explicitement les anciennes lignes simples Ens dans le brouillon v1 : leur conversion automatique est interdite. Confirmez que les coûts actuels sont connus, y compris lorsqu’ils sont nuls ; complétez d’abord les coûts inconnus.')) return;
            try { setSaving(true); await enableQuoteComposition(editor.quoteVersionId, editor.revision); setEditor(editorFromQuote(await getQuoteVersion(editor.id, editor.quoteVersionId))); }
            catch (failure) { reportRevisionError(failure); } finally { setSaving(false); }
          }}
          projects={projects}
          affairs={affairs}
          calculated={calculated}
          calculationStatus={calculationStatus}
          calculationError={calculationError}
          visibleLines={visibleLines}
          viewMode={viewMode}
          saving={saving}
          formatMoney={formatMoney}
          getProjectName={getProjectName}
          onUpdateEditor={updateEditor}
          onApplyCustomerSnapshot={applyCustomerSnapshot}
          onApplyPartnerTerms={applyPartnerTerms}
          onUpdateParameters={updateVersionParameters}
          onUpdateExpenses={updateVersionExpenses}
          onUpdateLine={updateLine}
          onApplyCatalogMaterial={applyCatalogMaterial}
          onAddVisibleLine={addVisibleLine}
          onAddTextLine={addTextLine}
          onAddSection={addSection}
          onUpdateSection={updateSection}
          onRemoveSection={removeSection}
          onImportSections={importSections}
          planningQuoteDraft={planningQuoteDraft}
          planningQuoteDraftLoading={planningQuoteDraftLoading}
          onImportPlanning={importPlanningIntoQuote}
          onRemoveLine={removeLine}
          onAddOption={addOption}
          onUpdateOption={updateOption}
          onRemoveOption={removeOption}
          onClearOptions={clearOptions}
          onAddOptionLine={addOptionLine}
          onUpdateOptionLine={updateOptionLine}
          onRemoveOptionLine={removeOptionLine}
          onDuplicateLine={duplicateLine}
          onMoveLine={moveLine}
          onForceLineValue={forceLineValue}
          onResetLineValue={resetLineValue}
          onVersionCommand={command => composition?.hasPendingChanges ? setError('Enregistrez les saisies de composition avant cette commande de version.') : runVersionCommand(command)}
          onPreviewDocument={previewDocument}
          onGenerateOfficialDocument={generateOfficialDocument}
          onArchiveDocumentGed={archiveDocumentToGed}
          onDownloadDocument={downloadDocument}
          onPreviewPrintTemplate={previewPrintTemplate}
          onPreviewInternalTemplate={previewInternalTemplate}
          onPrintInternalTemplate={printInternalTemplate}
          onPrintPdf={printPdf}
          onSave={saveQuote}
          onClose={closeEditor}
          onChangeViewMode={setViewMode}
        />
      ) : folder ? (
        <QuoteFolderView
          folder={folder}
          versions={folderVersions}
          documents={folderDocuments}
          history={folderHistory}
          formatMoney={formatMoney}
          onBack={clearFolder}
          onOpenVersion={openFolderVersion}
          onModifyCurrent={modifyCurrentVersion}
        />
      ) : (
        <QuoteList
          quotes={filteredQuotes}
          loading={loading}
          formatMoney={formatMoney}
          getProjectName={getProjectName}
          getQuoteProject={getQuoteProject}
          canCreate={Boolean(pagePermissions.canCreate)}
          onCreate={openNewQuote}
          onOpen={openExistingQuote}
        />
      )}
    </Box>
  );
}

export default QuotePage;
